import moment, { Moment } from 'moment';

import { Docking as DockingType } from '../../../types';
import { dockingInProgressEventStatusList } from '../../../utils/lists';
import { isNullOrUndefined } from '../../../utils/utils';

/**
 * A data que posiciona o evento na ordem, e o que ela é.
 *
 * Espelha docking_queries.event_start_expression() do tos-core: a data real
 * quando existir, a prevista caso contrário. O par de campos depende do
 * `purpose`, porque fundeio e atracação não são datados pelos mesmos campos.
 */
export type DockingEventStart = {
  label: string;
  date: string;
  isReal: boolean;
};

const ANCHORING_PURPOSES = ['ANCHORING_SERVICES', 'ANCHORING_WAIT'];

export function isAnchoringDocking(docking?: DockingType | null): boolean {
  return ANCHORING_PURPOSES.includes(docking?.purpose || '');
}

function formatEventDate(date: string | Moment | null | undefined) {
  if (isNullOrUndefined(date)) {
    return null;
  }
  return moment(date).format('DD/MM HH:mm');
}

export function getDockingEventStart(
  docking?: DockingType | null
): DockingEventStart | null {
  if (isNullOrUndefined(docking)) {
    return null;
  }

  const anchoring = isAnchoringDocking(docking);

  const realDate = formatEventDate(
    anchoring ? docking?.real_time_of_anchoring : docking?.first_cable_tied_in
  );
  if (realDate) {
    return {
      label: anchoring ? 'Fundeado' : '1º cabo',
      date: realDate,
      isReal: true,
    };
  }

  const expectedDate = formatEventDate(
    anchoring ? docking?.expected_anchoring : docking?.expected_berthing
  );
  if (expectedDate) {
    return { label: 'Previsto', date: expectedDate, isReal: false };
  }

  return null;
}

export function getDockingEventEnd(
  docking?: DockingType | null
): DockingEventStart | null {
  if (isNullOrUndefined(docking)) {
    return null;
  }

  const anchoring = isAnchoringDocking(docking);

  const realDate = formatEventDate(
    anchoring
      ? docking?.real_time_of_unanchoring
      : docking?.last_cable_untied_in
  );
  if (!realDate) {
    return null;
  }

  return {
    label: anchoring ? 'Saída do fundeio' : 'Último cabo',
    date: realDate,
    isReal: true,
  };
}

/** O berço, ou o fundeadouro, do jeito que o cabeçalho precisa exibir. */
export function getDockingPlaceLabel(docking?: DockingType | null): string {
  const place = docking?.docking_place?.name;
  if (place) {
    return place;
  }
  return isAnchoringDocking(docking)
    ? 'Fundeadouro não definido'
    : 'Berço não definido';
}

export function isDockingInProgress(docking?: DockingType | null): boolean {
  const eventStatus = docking?.event_status;
  return (
    !!eventStatus && dockingInProgressEventStatusList.includes(eventStatus)
  );
}
